/**
 * User Goal Service
 *
 * Service for managing user goals including creation, updating,
 * cancellation, locking, and resuming. Extends the base CrudService
 * with separate detail (show) and list (index) shapes.
 */

import { ZodError } from 'zod';

import {
  InvalidDataError,
  invalidDataErrorFromZod,
} from '../errors/invalid-data.error';
import { apiClient } from './api/client';
import { rethrowTyped } from './api/errors';
import { CrudService } from './crud.service';
import {
  validateUserGoal,
  type UserGoalInput,
} from '../validation/user-goal.schemas';

import type { ID } from '../types/common/id.types';
import type {
  UserGoalQueryParams,
  UserGoals,
  UserGoal,
} from '../types/domain/user-goal.types';

// =============================================================================
// USER GOAL SERVICE CLASS
// =============================================================================

export class UserGoalService extends CrudService<
  UserGoal,
  UserGoals,
  UserGoalInput
> {
  constructor(cacheTTL?: number) {
    super({
      baseUrl: '/goal',
      enableCaching: true,
      cacheTTL: cacheTTL || 5 * 60 * 1000, // 5 minutes default
      cacheVariesByUnit: true, // goal start/current/target values arrive in the user's unit
    });
  }

  // ---------------------------------------------------------------------------
  // CORE GOAL OPERATIONS
  // ---------------------------------------------------------------------------

  /**
   * Get goals for a given user.
   *
   * GET /api/user/{user}/goal
   */
  public async getUserGoals(
    username: string,
    params?: UserGoalQueryParams
  ): Promise<UserGoals[]> {
    if (!username) {
      throw new Error('Username is required to fetch user goals');
    }

    try {
      return await this.withContext({
        template: '/user/{username}/goal',
        parameters: { username },
        cacheNamespace: `${username}-goals`,
      }).getAll(params);
    } catch (error) {
      console.error(`Failed to fetch goals for user ${username}:`, error);
      rethrowTyped(error, 'Unable to fetch user goals. Please try again.');
    }
  }

  /**
   * Get detailed goal information by ID.
   *
   * GET /api/goal/{user_goal}
   */
  public async getGoalById(id: ID): Promise<UserGoal> {
    if (!id) {
      throw new Error('Goal ID is required');
    }

    try {
      const goal = await this.getById(id);
      return this.transformResponse(goal);
    } catch (error) {
      console.error(`Failed to fetch goal ${id}:`, error);
      rethrowTyped(
        error,
        'Unable to fetch goal details. Please check the goal ID and try again.'
      );
    }
  }

  /**
   * Create a new goal for a user.
   *
   * POST /api/user/{user}/goal
   * Uses user-goal.schemas.ts for client-side validation.
   */
  public async createUserGoal(
    username: string,
    data: UserGoalInput
  ): Promise<UserGoals> {
    if (!username) {
      throw new Error('Username is required to create a user goal');
    }

    try {
      this.validateGoalData(data);

      const transformedData = this.transformRequest(data);

      const created = await this.withContext({
        template: '/user/{username}/goal',
        parameters: { username },
        cacheNamespace: `${username}-goals`,
      }).create(transformedData);

      return created;
    } catch (error) {
      console.error('Failed to create user goal:', error);
      rethrowTyped(
        error,
        'Unable to create user goal. Please check your data and try again.'
      );
    }
  }

  /**
   * Update an existing goal by ID.
   *
   * PUT/PATCH /api/goal/{user_goal}
   * Uses user-goal.schemas.ts for client-side validation.
   */
  public async updateUserGoal(id: ID, data: UserGoalInput): Promise<UserGoals> {
    if (!id) {
      throw new Error('Goal ID is required to update a user goal');
    }

    try {
      this.validateGoalData(data);

      const transformed = this.transformRequest(data);
      const updated = await this.update(id, transformed, { partial: true });

      return updated;
    } catch (error) {
      console.error(`Failed to update user goal ${id}:`, error);
      rethrowTyped(
        error,
        'Unable to update user goal. Please check your data and try again.'
      );
    }
  }

  /**
   * Delete a goal by ID.
   *
   * DELETE /api/goal/{user_goal}
   */
  public async deleteUserGoal(id: ID): Promise<void> {
    if (!id) {
      throw new Error('Goal ID is required to delete a user goal');
    }

    try {
      await this.delete(id);
    } catch (error) {
      console.error(`Failed to delete user goal ${id}:`, error);
      rethrowTyped(error, 'Unable to delete user goal. Please try again.');
    }
  }

  /**
   * Restore a soft-deleted goal.
   *
   * PATCH /api/undo-delete/goal/{user_goal}
   */
  public async restoreUserGoal(id: ID): Promise<void> {
    if (!id) {
      throw new Error('Goal ID is required to restore a user goal');
    }

    try {
      await this.undoDelete('goal', id);
    } catch (error) {
      console.error(`Failed to restore user goal ${id}:`, error);
      rethrowTyped(error, 'Unable to restore goal. Please try again.');
    }
  }

  // ---------------------------------------------------------------------------
  // GOAL STATE TRANSITIONS (CANCEL, LOCK, RESUME)
  // ---------------------------------------------------------------------------
  // NOTE:
  // These endpoints return no body (204 or 200 with empty response), which
  // does not fit CrudService.update’s expectation of a non-empty response.
  // To avoid abusing the CRUD methods and throwing false errors, these are
  // treated as the rare, “absolutely necessary” direct apiClient calls.
  //
  // Being contextless, their invalidation widens across namespaces —
  // `getUserGoals` caches the list under `{username}-goals`, which a scoped
  // invalidation would match `getAll…` against and miss entirely.

  private getGoalActionUrl(
    id: ID,
    action: 'cancel' | 'lock' | 'resume'
  ): string {
    return `/goal/${id}/${action}`;
  }

  /**
   * Cancel a goal.
   *
   * PATCH /api/goal/{user_goal}/cancel
   * Backend returns 204 No Content on success.
   */
  public async cancelGoal(id: ID): Promise<void> {
    if (!id) {
      throw new Error('Goal ID is required to cancel a goal');
    }

    try {
      await apiClient.patch(this.getGoalActionUrl(id, 'cancel'));
      this.invalidateCache(
        ['getAll', 'getPaginated', 'getById', 'search', 'count'],
        id
      );
    } catch (error) {
      console.error(`Failed to cancel goal ${id}:`, error);
      rethrowTyped(error, 'Unable to cancel goal. Please try again.');
    }
  }

  /**
   * Lock a goal.
   *
   * PATCH /api/goal/{user_goal}/lock
   * Backend returns 200 OK with no data.
   */
  public async lockGoal(id: ID): Promise<void> {
    if (!id) {
      throw new Error('Goal ID is required to lock a goal');
    }

    try {
      await apiClient.patch(this.getGoalActionUrl(id, 'lock'));
      this.invalidateCache(
        ['getAll', 'getPaginated', 'getById', 'search', 'count'],
        id
      );
    } catch (error) {
      console.error(`Failed to lock goal ${id}:`, error);
      rethrowTyped(error, 'Unable to lock goal. Please try again.');
    }
  }

  /**
   * Resume a previously canceled goal.
   *
   * PATCH /api/goal/{user_goal}/resume
   * Backend returns 204 No Content on success.
   */
  public async resumeGoal(id: ID): Promise<void> {
    if (!id) {
      throw new Error('Goal ID is required to resume a goal');
    }

    try {
      await apiClient.patch(this.getGoalActionUrl(id, 'resume'));
      this.invalidateCache(
        ['getAll', 'getPaginated', 'getById', 'search', 'count'],
        id
      );
    } catch (error) {
      console.error(`Failed to resume goal ${id}:`, error);
      rethrowTyped(error, 'Unable to resume goal. Please try again.');
    }
  }

  // ---------------------------------------------------------------------------
  // VALIDATION AND TRANSFORMATION
  // ---------------------------------------------------------------------------

  /**
   * Validate goal payload using Zod schema from user-goal.schemas.ts.
   */
  protected validateGoalData(data: UserGoalInput): void {
    try {
      validateUserGoal(data);
    } catch (error) {
      if (error instanceof ZodError) {
        throw invalidDataErrorFromZod("Couldn't save the goal", error, {
          originalData: data,
          details: { service: 'UserGoalService' },
        });
      }

      throw new InvalidDataError(
        "Couldn't save the goal: the data is in an unexpected format",
        {
          originalData: data,
          details: { service: 'UserGoalService' },
        }
      );
    }
  }

  /**
   * Transform request data before sending to API
   * Formats data according to API expectations
   *
   * @param data - Original request data
   * @returns Transformed request data
   */
  protected override transformRequest(
    data: Partial<UserGoalInput>
  ): Partial<UserGoalInput> {
    return {
      ...data,
    };
  }

  /**
   * Normalize response from API for frontend consumption.
   * Currently returns data as-is.
   */
  protected override transformResponse(data: UserGoal): UserGoal {
    return {
      ...data,
    };
  }
}

// =============================================================================
// SERVICE INSTANCE
// =============================================================================

/**
 * Default user goal service instance.
 */
export const userGoalService = new UserGoalService();
